import { Store } from '@ngxs/store';
import { isNil } from 'lodash';
import { ContentBlock } from 'src/app/content-blocks/models/content-block';
import { ContentBlockTypes } from 'src/app/content-blocks/models/content-block-types';
import { ImageBlock } from 'src/app/content-blocks/models/images-block';
import { MarkdownBlock } from 'src/app/content-blocks/models/markdown-block';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { QuestionSubmission } from 'src/app/quiz/models/question-submission.model';
import { QuizLifecycleState } from 'src/app/quiz/models/quiz-lifecycle.enum';
import { QuizQuestionType } from 'src/app/quiz/models/quiz-question-type.enum';
import {
  LabelData,
  MultipleChoiceData,
  ObjectSelectData,
  QuizOption,
  QuizQuestion,
} from 'src/app/quiz/models/quiz-question.model';
import { QuizActions } from 'src/app/quiz/state/quiz.actions';
import { QuizState } from 'src/app/quiz/state/quiz.state';
import { QuizService } from 'src/app/view/components/view-quiz/view-quiz.service';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { StoryState } from 'src/app/story/state/story.state';
import { RPCEvent } from '../define-event';
import { Procedure } from '../define-procedure';
import { MessageEnum } from '../models/message.enum';
import { QuizIO } from '../models/quiz.io';
import { createQuestionsFromChapters, isAnswerCorrect, scoreQuiz } from 'src/app/quiz/config/quiz';
import { state } from '@angular/animations';

function getQuestions(store: Store): QuizQuestion[] {
  return store.selectSnapshot(QuizState.getAllQuestions);
}

function getCurrentQuestion(store: Store): QuizQuestion {
  return getQuestions(store).find((q) => q.id === getCurrentQuestionId(store));
}

function getCurrentAnswer(store: Store): QuestionSubmission {
  const submissions = getSubmissions(store);
  return submissions.find((s) => s.questionId === getCurrentQuestionId(store));
}

function formatQuizState(store: Store): QuizIO.QuizInfoResponse {
  return {
    active: store.selectSnapshot(QuizState.getQuizModeActive),
    questions: convertQuestionsToResponse(getQuestions(store)),
    questionId: getCurrentQuestionId(store),
    submissions: convertSubmissionsToResponse(store, getSubmissions(store)),
  };
}

function convertSubmissionsToResponse(
  store: Store,
  submissions: QuestionSubmission[]
): QuizIO.SubmissionResponse[] {
  return submissions.map((submission) =>
    convertSubmissionToResponse(store, submission)
  );
}

function convertSubmissionToResponse(
  store: Store,
  submission: QuestionSubmission
): QuizIO.SubmissionResponse {
  const question = getCurrentQuestion(store);
  const questionId = submission.questionId;

  return {
    questionId: questionId,
    answerIds: question.data.correctAnswer,
    correct: isAnswerCorrect(question, submission.answer),
  };
}

function getSubmissions(store: Store): QuestionSubmission[] {
  return store.selectSnapshot(QuizState.getAllSubmissions);
}

function getCurrentQuestionId(store: Store): string {
  return store.selectSnapshot(QuizState.getActiveQuestionId);
}

function getCurrent(store: Store) {
  const question = getCurrentQuestion(store);
  const answer = getCurrentAnswer(store);
  return {
    question: convertQuestionToResponse(question),
    submission: answer ? convertSubmissionToResponse(store, answer) : undefined,
  };
}

function contentBlocksToContentResponses(
  contentBlocks: ContentBlock[]
): QuizIO.Content[] {
  return contentBlocks.map((contentBlock) =>
    contentBlockToContentResponse(contentBlock)
  );
}

function contentBlockToContentResponse(
  contentBlock: ContentBlock
): QuizIO.Content {
  const type = contentBlock.type;
  switch (type) {
    case ContentBlockTypes.Markdown:
      const { markdown } = contentBlock as MarkdownBlock;
      return {
        type,
        content: markdown,
      };
    case ContentBlockTypes.Images:
      const { images } = contentBlock as ImageBlock;
      const imageURLs = images.map((image) => image.urls.default);
      return {
        type,
        content: imageURLs,
      };
    default:
      return {
        type,
        content: '',
      };
  }
}

function optionsToResponse(options: QuizOption[]): QuizIO.Answer[] {
  return options.map((option) => convertOptionToResponse(option));
}

function convertOptionToResponse(option: QuizOption): QuizIO.Answer {
  return {
    answerId: option.value,
    displayText: option.label,
  };
}

function convertQuestionToResponse(question: QuizQuestion): QuizIO.Question {
  let answers: QuizIO.Answer[] = [];
  let answerId = '';

  // type specific answers
  switch (question.type) {
    case QuizQuestionType.MultipleChoice:
      const multipleChoiceData: MultipleChoiceData = question.data;
      answers = optionsToResponse(multipleChoiceData.options);
      answerId = multipleChoiceData.correctAnswer;
      break;

    case QuizQuestionType.SelectAnatomy:
      const selectAnatomyData: ObjectSelectData = question.data;
      answers = selectAnatomyData.correctAnswer.map((answer) => {
        return {
          answerId: answer,
          displayText: answer,
        };
      });
      // TODO: expose answerId as a list
      answerId = '';
      break;

    case QuizQuestionType.LabelAnatomy:
      const labelData: LabelData = question.data;
      answers = labelData.options.map((answer) => {
        return {
          answerId: answer.value,
          displayText: answer.label.title,
        };
      });
      // TODO: expose answerId as a list
      answerId = '';
      break;
  }

  return {
    questionId: question.id,
    questionType: question.type,
    questionPrompt: contentBlocksToContentResponses(question.questionPrompt),
    answerExplanation: contentBlocksToContentResponses(question.explanation),
    answers: answers,
    answerId: answerId,
  };
}

function convertQuestionsToResponse(
  questions: QuizQuestion[],
  questionIds?: string[]
): QuizIO.Question[] {
  const responses = questions.map((question) =>
    convertQuestionToResponse(question)
  );

  // filter by question ids
  if (typeof questionIds !== 'undefined') {
    return responses.filter((question) =>
      questionIds.includes(question.questionId)
    );
  } else {
    return responses;
  }
}

export function getQuizEvents(
  store: Store
): RPCEvent<QuizIO.QuizEventResponse>[] {
  return [
    {
      eventName: EventsEnum.QuizEntered,
      mapFn: () => formatQuizState(store),
    },
    {
      eventName: EventsEnum.QuizExited,
      mapFn: () => true,
    },
    {
      eventName: EventsEnum.QuizQuestionLoaded,
      mapFn: (question: QuizQuestion) => question,
    }, 
    {
      // TODO: reconcile string vs array of strings return type
      // only vanilla mc return a string
      // for label multiple choice, selection is being emitted twice. likely because of form change subscription.
      eventName: EventsEnum.QuizAnswerSelected,
      mapFn: (answer: QuestionSubmission) => answer,
    },
    {
      eventName: EventsEnum.QuizAnswerSubmitted,
      mapFn: (submission: QuestionSubmission) => {
        const question = store
          .selectSnapshot(QuizState)
          .questions.find(
            (_question) => _question.id === submission.questionId
          );

        return {
          questionId: submission.questionId,
          answerIds: submission.answer,
          correct: isAnswerCorrect(
            question,
            submission.answer
          ),
        };
      },
    },
    {
      eventName: EventsEnum.QuizCompleted,
      mapFn: () => {
        const questions = store.selectSnapshot(QuizState.getAllQuestions);
        const submissions = store.selectSnapshot(QuizState.getAllSubmissions);
        return scoreQuiz(questions, submissions);
      },
    },
  ];
}


export function getQuizProcedures(
  store: Store, quizService: QuizService
): Procedure<QuizIO.QuizRequest, QuizIO.QuizResponse>[] {
  
  function guardQuizModeInactive(handler) {
    const quizModeActive = store.selectSnapshot(QuizState.getQuizModeActive);
    if (!quizModeActive) {
      return { error: 'quiz mode is not active' };
    }
    return handler();
  }

  return [
    {
      procName: MessageEnum.QuizInfo,
      actionFn: () => guardQuizModeInactive(() => {
        return formatQuizState(store);
      }),
    },
    {
      procName: MessageEnum.QuizSubmit,
      actionFn: (request: QuizIO.QuizSubmitRequest) => {
        return guardQuizModeInactive(() => {
          quizService.submitQuestion(request.questionId, request.answerIds);
          return formatQuizState(store);
        });
      },
    },
    {
      procName: MessageEnum.QuizQuestion,
      actionFn: () => guardQuizModeInactive(() => {
        return getCurrent(store);
      })
    },
    {
      procName: MessageEnum.QuizEnter,
      actionFn: () => {
        if (store.selectSnapshot(QuizState.hasQuiz)) {
          quizService.startQuiz();
          return true;
        } else {
          return {error: 'quiz not found'};
        }
      }
    },
    {
      procName: MessageEnum.QuizExit,
      actionFn: () => {
        quizService.exitQuiz();
        return true;
      },
    },
    {
      procName: MessageEnum.QuizSelectQuestion,
      actionFn: (questionId: string) => {
        return guardQuizModeInactive(() => {
          store.dispatch(new QuizActions.SetActiveQuestion(questionId));
          return formatQuizState(store);
        });
      },
    },
    {
      procName: MessageEnum.QuizNextQuestion,
      actionFn: () => {
        return guardQuizModeInactive(() => {
          quizService.nextQuestion();
          return getCurrent(store);
        });
      },
    },
    {
      procName: MessageEnum.QuizPreviousQuestion,
      actionFn: () => guardQuizModeInactive(() => {
        store.dispatch(new QuizActions.SetQuizLifecycle(QuizLifecycleState.Question));
        store.dispatch(new QuizActions.PreviousQuestion());
        return getCurrent(store);
      })
    },
    {
      procName: MessageEnum.QuizReset,
      actionFn: () => {
        quizService.resetQuiz();
        return formatQuizState(store);
      }
  },
    {
      procName: MessageEnum.QuizSet,
      actionFn: (params: QuizIO.QuizSetModel) => {
        const { settings, startNumber, endNumber, locked } = params;

        // set settings
        if (!isNil(settings)) {
          const currentSettings = store.selectSnapshot(QuizState.getQuizSettings);
          store.dispatch(new QuizActions.SetQuizSettings({
            ...currentSettings,
            ...settings
          }));
        }

        // set questions
        if (typeof startNumber === 'number' || typeof endNumber === 'number') {
          const chapters = store.selectSnapshot(StoryChaptersState.getStoryChapters);
          const questions = createQuestionsFromChapters(chapters);
          const startIndex = typeof startNumber === 'number'
            ? startNumber - 1
            : 0;
          const endIndex = typeof endNumber === 'number'
            ? endNumber
            : questions.length;

          const apiQuestions = questions.slice(startIndex, endIndex);
          store.dispatch(new QuizActions.SetQuestions(apiQuestions));
        }
        
        // set locked
        if (typeof locked === 'boolean') {
          store.dispatch(new QuizActions.SetQuizLocked(locked));
        }

        // set questions mode
        const quizQuestionsMode = typeof startNumber === 'number' && typeof endNumber === 'number' ? 'restricted' : 'all';
        store.dispatch(new QuizActions.SetQuizQuestionsMode(quizQuestionsMode));

        return formatQuizState(store);
      }
    }
  ];
}
